/**
 * Clipboard utility with graceful fallback for restricted environments
 * Handles clipboard API permissions and provides alternative copy methods
 */

import { toast } from 'sonner';

/**
 * Copy text to clipboard with fallback mechanism
 * @param {string} text - Text to copy
 * @param {string} successMessage - Optional success message
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboard = async (text, successMessage = 'Copied to clipboard!') => {
  // Method 1: Try Clipboard API first (modern browsers)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, trying fallback...', err);
      // Fall through to fallback methods
    }
  }

  // Method 2: Fallback using execCommand (deprecated but works in more scenarios)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      toast.success(successMessage);
      return true;
    }
  } catch (err) {
    console.warn('execCommand fallback failed', err);
  }

  // Method 3: Show manual copy dialog as last resort
  showManualCopyDialog(text);
  return false;
};

/**
 * Show a dialog for manual copy when automated methods fail
 * @param {string} text - Text to display for manual copying
 */
const showManualCopyDialog = (text) => {
  // Create modal overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
  `;

  // Create modal content
  const modal = document.createElement('div');
  modal.style.cssText = `
    background: white;
    padding: 24px;
    border-radius: 8px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  `;

  modal.innerHTML = `
    <div style="margin-bottom: 16px;">
      <h3 style="font-size: 18px; font-weight: 600; margin-bottom: 8px; color: #0F52BA;">
        📋 Copy this text
      </h3>
      <p style="font-size: 14px; color: #666; margin-bottom: 16px;">
        Automatic clipboard access is blocked. Please copy the text manually:
      </p>
      <div style="background: #f5f5f5; border: 1px solid #ddd; border-radius: 4px; padding: 12px; margin-bottom: 16px;">
        <input
          type="text"
          value="${text.replace(/"/g, '&quot;')}"
          readonly
          style="width: 100%; border: none; background: transparent; font-family: monospace; font-size: 14px; outline: none;"
          id="manual-copy-input"
        />
      </div>
      <div style="display: flex; gap: 8px; justify-content: flex-end;">
        <button
          id="select-copy-btn"
          style="padding: 8px 16px; background: #0F52BA; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
        >
          Select & Copy
        </button>
        <button
          id="close-copy-dialog"
          style="padding: 8px 16px; background: #e5e7eb; color: #374151; border: none; border-radius: 4px; cursor: pointer; font-size: 14px;"
        >
          Close
        </button>
      </div>
    </div>
  `;

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Auto-select the text
  const input = document.getElementById('manual-copy-input');
  input.select();
  input.focus();

  // Select and copy button
  document.getElementById('select-copy-btn').onclick = () => {
    input.select();
    try {
      document.execCommand('copy');
      toast.success('Text selected! Press Ctrl+C (Cmd+C on Mac) to copy');
    } catch (err) {
      toast.info('Text selected. Press Ctrl+C (Cmd+C on Mac) to copy');
    }
  };

  // Close button
  document.getElementById('close-copy-dialog').onclick = () => {
    document.body.removeChild(overlay);
  };

  // Close on overlay click
  overlay.onclick = (e) => {
    if (e.target === overlay) {
      document.body.removeChild(overlay);
    }
  };

  toast.info('Clipboard access blocked. Please copy manually from the dialog.');
};

/**
 * Check if clipboard API is available
 * @returns {boolean}
 */
export const isClipboardAvailable = () => {
  return !!(navigator.clipboard && navigator.clipboard.writeText);
};
